import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_URLS } from '../config/api';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({});
  const [products, setProducts] = useState([]);
  const [cartProductsCache, setCartProductsCache] = useState({}); // 购物车商品信息缓存
  const [cartAddOrder, setCartAddOrder] = useState([]); // 记录商品添加到购物车的顺序
  const [cartAddTime, setCartAddTime] = useState({}); // 记录商品添加时间
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [tableNums, setTableNums] = useState([]);
  const [selectedTableNum, setSelectedTableNum] = useState('');
  const [loading, setLoading] = useState(false);
  const [orderDetailsCache, setOrderDetailsCache] = useState({}); // 订单详情缓存

  // 移除重复的桌台列表获取，改为由外部组件主动更新
  // useEffect(() => {
  //   async function fetchTableNumbers() {
  //     try {
  //       const res = await fetch(API_URLS.TABLE_NUMBER_LIST, {
  //         method: 'POST',
  //         headers: {
  //           'Content-Type': 'application/json',
  //         },
  //         body: JSON.stringify({}),
  //       });
  //       const data = await res.json();
  //       if (data.code === 200 && data.data && data.data.length > 0) {
  //         setTableNumbers(data.data);
  //         setSelectedTableNumber(data.data[0].tableNumber);
  //       }
  //     } catch (e) {
  //       setTableNumbers([]);
  //     }
  //   }
  //   fetchTableNumbers();
  // }, []);

  // 提供函数让外部组件更新桌台列表
  const updateTableNums = useCallback((newTableNums) => {
    setTableNums(newTableNums);
    // 如果还没有选中桌台且有可用桌台，设置默认桌台
    if (!selectedTableNum && newTableNums.length > 0) {
      setSelectedTableNum(newTableNums[0].tableNum);
    }
  }, [selectedTableNum]);

  // 获取商品分类列表 - 移除selectedCategory依赖，避免循环调用
  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch(API_URLS.PRODUCT_TYPE_LIST, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (data.code === 200 && data.data && data.data.length > 0) {
        // 按sequence字段排序
        const sortedCategories = data.data.sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
        setCategories(sortedCategories);
        // 只在没有选中分类时设置默认分类，避免重复设置
        setSelectedCategory(prevSelected => {
          if (!prevSelected && sortedCategories.length > 0) {
            return sortedCategories[0].id;
          }
          return prevSelected;
        });
      }
    } catch (error) {
      console.error('获取商品分类失败:', error);
    }
  }, []);

  // 调用API获取商品数据
  const fetchProducts = useCallback(async (keyword = '', categoryId = null) => {
    setLoading(true);
    try {
      const requestBody = {};
      
      // 如果有搜索关键词，使用title参数
      if (keyword.trim()) {
        requestBody.title = keyword;
      }
      // 如果有分类ID，使用type参数
      else if (categoryId) {
        requestBody.type = parseInt(categoryId, 10);
      }
      // 如果没有关键词但有选中的分类，使用选中分类的ID
      else if (selectedCategory) {
        requestBody.type = parseInt(selectedCategory, 10);
      }

      const response = await fetch(API_URLS.PRODUCT_LIST, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });
      const data = await response.json();
      if (data.code === 200 && data.data) {
        setProducts(data.data);
        
        // 更新购物车商品缓存 - 将新获取的商品信息添加到缓存中
        setCartProductsCache(prevCache => {
          const newCache = { ...prevCache };
          data.data.forEach(product => {
            newCache[product.id] = product;
          });
          return newCache;
        });
      }
    } catch (error) {
      console.error('API调用失败:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  // 初始化加载分类数据 - 只调用一次
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // 当选中分类改变时，重新获取商品数据
  useEffect(() => {
    if (selectedCategory) {
      fetchProducts('', selectedCategory);
    }
  }, [selectedCategory, fetchProducts]);

  // 添加到购物车
  const addToCart = (productId) => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    const timeString = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    
    setCart(prev => ({ ...prev, [productId]: (prev[productId] || 0) + 1 }));
    
    // 记录商品添加顺序 - 只在第一次添加时记录
    setCartAddOrder(prev => {
      if (!prev.includes(productId)) {
        return [...prev, productId];
      }
      return prev;
    });
    
    // 记录商品添加时间 - 只在第一次添加时记录
    setCartAddTime(prev => {
      if (!prev[productId]) {
        return { ...prev, [productId]: timeString };
      }
      return prev;
    });
    
    // 确保商品信息在缓存中
    const product = products.find(p => p.id === productId);
    if (product && !cartProductsCache[productId]) {
      setCartProductsCache(prev => ({
        ...prev,
        [productId]: product
      }));
    }
  };

  // 从购物车移除
  const removeFromCart = (productId) => {
    setCart(prev => {
      const newCart = { ...prev };
      const newCount = Math.max(0, (newCart[productId] || 0) - 1);
      
      if (newCount === 0) {
        // 如果商品数量变为0，从添加顺序中移除
        setCartAddOrder(prevOrder => prevOrder.filter(id => id !== productId));
        // 从添加时间记录中移除
        setCartAddTime(prevTime => {
          const newTime = { ...prevTime };
          delete newTime[productId];
          return newTime;
        });
        delete newCart[productId];
      } else {
        newCart[productId] = newCount;
      }
      
      return newCart;
    });
  };

  // 清空购物车
  const clearCart = () => {
    setCart({});
    setCartAddOrder([]); // 清空添加顺序记录
    setCartAddTime({}); // 清空添加时间记录
    // 注意：不清空cartProductsCache，保留商品信息以便后续使用
  };

  // 获取购物车中的商品列表 - 使用缓存的商品信息，按添加顺序排列
  const getCartItems = () => {
    return cartAddOrder
      .filter(productId => cart[productId] && cart[productId] > 0) // 只包含有效的购物车商品
      .map(productId => {
        // 优先从缓存中获取商品信息，如果缓存中没有则从当前products中获取
        const product = cartProductsCache[productId] || products.find(p => p.id === Number(productId));
        return { 
          ...product, 
          count: cart[productId],
          addTime: cartAddTime[productId] || '' // 添加商品添加时间
        };
      })
      .filter(item => item.id); // 过滤掉找不到的商品
  };

  // 根据桌号获取openNum
  const getOpenNum = (tableNum) => {
    const table = tableNums.find(t => t.tableNum === tableNum);
    if (!table) {
      return '';
    }
    
    // 尝试不同的字段名，按优先级排序
    return table.openNum || 
           table.openNumber || 
           table.openNo || 
           table.tableOpenNum || 
           table.id || 
           '';
  };

  // 设置订单详情缓存
  const setOrderDetailsForOpenNum = (openNum, details) => {
    setOrderDetailsCache(prev => ({
      ...prev,
      [openNum]: details
    }));
  };

  // 获取订单详情缓存
  const getOrderDetailsForOpenNum = (openNum) => {
    return orderDetailsCache[openNum] || null;
  };

  // 清除订单详情缓存
  const clearOrderDetailsCache = () => {
    setOrderDetailsCache({});
  };

  // 计算购物车总价和数量 - 使用缓存的商品信息
  const totalPrice = Object.entries(cart).reduce((sum, [id, count]) => {
    const product = cartProductsCache[id] || products.find(p => p.id === Number(id));
    return sum + (product ? Number(product.price) * count : 0);
  }, 0);

  const totalCount = Object.values(cart).reduce((a, b) => a + b, 0);

  const value = {
    // 状态
    cart,
    products,
    categories,
    selectedCategory,
    tableNums,
    selectedTableNum,
    selectedTableNumber: selectedTableNum, // 添加别名
    loading,
    totalPrice,
    totalCount,
    
    // 方法
    setSelectedCategory,
    setSelectedTableNum,
    setSelectedTableNumber: setSelectedTableNum, // 添加别名
    fetchProducts,
    addToCart,
    removeFromCart,
    clearCart,
    getCartItems,
    getOpenNum,
    setOrderDetailsForOpenNum,
    getOrderDetailsForOpenNum,
    clearOrderDetailsCache,
    updateTableNums // 添加更新桌台列表的函数
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}; 